import type { PolicyType, PublicRoomView } from "@secret-cabinet/contracts";
import { Crown, Eye, Gavel, Search, Shuffle, Skull, Scale, Vote } from "lucide-react";

export function PublicBoard({ room, compact = false }: { room: PublicRoomView; compact?: boolean }) {
  return (
    <div className={`public-board${compact ? " public-board--compact" : ""}`}>
      <PolicyTracks room={room} />
      <div className="public-board__lower">
        <GovernmentDesk room={room} />
        <ElectionTracker room={room} />
      </div>
    </div>
  );
}

export function PolicyTracks({ room }: { room: PublicRoomView }) {
  return (
    <div className="policy-tracks" aria-label="Policy tracks">
      <PolicyTrack
        type="liberal"
        enacted={room.policies.liberal}
        goal={room.policies.liberalGoal}
        powers={Array.from({ length: room.policies.liberalGoal }, () => null)}
        executionName={null}
        executionPolicyIndex={null}
      />
      <PolicyTrack
        type="fascist"
        enacted={room.policies.fascist}
        goal={room.policies.fascistGoal}
        powers={fascistPowerLabels(room.players.length)}
        executionName={room.lastExecution?.playerName ?? null}
        executionPolicyIndex={room.lastExecution?.policyIndex ?? null}
      />
      <p className="deck-count" aria-label="Full policy deck composition">
        Full deck <b>{room.policies.liberalDeckSize} Liberal · {room.policies.fascistDeckSize} Fascist</b>
      </p>
    </div>
  );
}

function PolicyTrack({
  type,
  enacted,
  goal,
  powers,
  executionName,
  executionPolicyIndex,
}: {
  type: PolicyType;
  enacted: number;
  goal: number;
  powers: Array<string | null>;
  executionName: string | null;
  executionPolicyIndex: number | null;
}) {
  const executionIndex = executionPolicyIndex === null ? -1 : executionPolicyIndex - 1;
  return (
    <section className={`policy-track policy-track--${type}`}>
      <header>
        <span>{type === "liberal" ? "Liberal statute" : "Fascist decree"}</span>
        <b>{enacted} / {goal}</b>
      </header>
      <div className="policy-track__slots">
        {Array.from({ length: goal }, (_, index) => {
          const filled = index < enacted;
          return (
            <span
              className={`policy-slot${filled ? " policy-slot--filled" : ""}`}
              key={`${type}-${index}`}
              aria-label={`${capitalise(type)} policy ${index + 1}${filled ? " enacted" : " empty"}`}
            >
              <i>{filled ? "ENACTED" : String(index + 1).padStart(2, "0")}</i>
              {powers[index] ? (
                <small>
                  <PowerIcon label={powers[index]!} />
                  {powers[index]}
                  {index === executionIndex && executionName ? ` · ${executionName}` : ""}
                </small>
              ) : null}
            </span>
          );
        })}
      </div>
    </section>
  );
}

export function GovernmentDesk({ room }: { room: PublicRoomView }) {
  const president = room.players.find((player) => player.id === room.presidentCandidateId);
  const chancellor = room.players.find((player) => player.id === room.chancellorCandidateId);
  const showGovernment = room.phase !== "lobby" && room.phase !== "role-reveal";

  return (
    <section className="government-desk" aria-label="Proposed government">
      <header>
        <span><Scale aria-hidden="true" /> Government desk</span>
        {showVoteLedger(room) ? (
          <b>Revealed {room.lastGovernment.ballotsRevealed} / {room.lastGovernment.ballotsTotal} · {room.lastGovernment.ja} Ja · {room.lastGovernment.nein} Nein</b>
        ) : null}
      </header>
      {showGovernment ? (
        <div className="office-placards">
          <div className="office-placard">
            <Crown aria-hidden="true" />
            <small>President</small>
            <strong>{president?.name ?? "Pending"}</strong>
          </div>
          <span className="office-divider" aria-hidden="true">×</span>
          <div className="office-placard">
            <Gavel aria-hidden="true" />
            <small>{chancellorOfficeLabel(room)}</small>
            <strong>{chancellor?.name ?? "Not nominated"}</strong>
          </div>
        </div>
      ) : (
        <p className="government-empty">Offices are assigned after every dossier is sealed.</p>
      )}
    </section>
  );
}

export function ElectionTracker({ room }: { room: PublicRoomView }) {
  return (
    <section className="election-tracker" aria-label="Election tracker">
      <header>
        <span><Vote aria-hidden="true" /> Election pressure</span>
        <b>{room.electionTracker} / {room.electionTrackerLimit}</b>
      </header>
      <div className="election-tracker__rail">
        {Array.from({ length: room.electionTrackerLimit }, (_, index) => (
          <span
            className={index < room.electionTracker ? "is-active" : ""}
            key={index}
            aria-label={`Failed government ${index + 1}${index < room.electionTracker ? " marked" : " unmarked"}`}
          >
            {index + 1}
          </span>
        ))}
      </div>
      <p>{room.electionTracker === 3 ? "Chaos is pending." : "Three failed governments enact the top policy."}</p>
    </section>
  );
}

function fascistPowerLabels(playerCount: number): Array<string | null> {
  if (playerCount <= 6) {
    return [null, null, "Peek", "Execute", "Execute + veto", "Fascist win"];
  }
  if (playerCount <= 8) {
    return [null, "Investigate", "Special vote", "Execute", "Execute + veto", "Fascist win"];
  }
  return ["Investigate", "Investigate", "Special vote", "Execute", "Execute + veto", "Fascist win"];
}

function chancellorOfficeLabel(room: PublicRoomView): string {
  if (room.phase === "election") {
    return "Chancellor candidate";
  }
  if (room.phase === "election-result" && !room.lastGovernment?.passed) {
    return "Chancellor candidate";
  }
  return "Chancellor";
}

function capitalise(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function showVoteLedger(room: PublicRoomView): room is PublicRoomView & {
  lastGovernment: NonNullable<PublicRoomView["lastGovernment"]>;
} {
  return Boolean(
    room.lastGovernment &&
      (room.lastGovernment.ballots.length > 0 ||
        (room.phase === "election-result" && room.phaseResult !== "veto-accepted") ||
        (room.phase === "game-over" && room.winner?.reason === "hitler-elected")),
  );
}

function PowerIcon({ label }: { label: string }) {
  if (label === "Investigate") return <Search aria-hidden="true" />;
  if (label === "Peek") return <Eye aria-hidden="true" />;
  if (label === "Special vote") return <Shuffle aria-hidden="true" />;
  if (label.startsWith("Execute")) return <Skull aria-hidden="true" />;
  return null;
}
